import { useState } from "react";
import { useWizard } from "../wizard-state";
import { Cie10Search } from "@features/cie10/Cie10Search";
import { CIRUGIAS_COMUNES } from "@/data/cirugias";
import type { DiagnosticoCie10 } from "@/types/wizard";

type Props = { errors: Set<string> };

const TIPOS_ANESTESIA = [
  "Local",
  "Local con sedación",
  "Regional (peribulbar)",
  "Regional (retrobulbar)",
  "General",
  "Tópica",
  "No requiere",
];

export function Step1Basicos({ errors }: Props) {
  const { data, set } = useWizard();
  const invalid = (k: string) => errors.has(k) ? "campo-requerido-error" : "";

  const [showExtraForm, setShowExtraForm] = useState(false);

  const addDiagnostico = (code: string, description: string) => {
    if (!code) return;
    // Evita duplicados (mismo código ya principal o ya en la lista)
    if (code === data.cie10) return;
    if (data.diagnosticosExtra.some((dx) => dx.code === code)) return;
    const next: DiagnosticoCie10[] = [...data.diagnosticosExtra, { code, description }];
    set("diagnosticosExtra", next);
    setShowExtraForm(false);
  };

  const removeExtra = (idx: number) => {
    set("diagnosticosExtra", data.diagnosticosExtra.filter((_, i) => i !== idx));
  };

  return (
    <div className="wizard-grid">
      <label className="form-field">
        <span>Documento *</span>
        <input value={data.doc} onChange={(e) => set("doc", e.target.value)} className={invalid("doc")} />
      </label>

      <label className="form-field">
        <span>Nombre del paciente *</span>
        <input value={data.nombre} onChange={(e) => set("nombre", e.target.value)} className={invalid("nombre")} />
      </label>

      <label className="form-field">
        <span>Edad</span>
        <input
          type="number"
          min="0"
          max="120"
          value={data.edad}
          onChange={(e) => {
            const v = e.target.value;
            set("edad", v);
            // Marca automáticamente el flag de menor de edad
            const n = Number(v);
            if (v && !isNaN(n)) set("esMenorEdad", n < 18);
          }}
        />
      </label>

      <label className="form-field">
        <span>EPS / Aseguradora</span>
        <input value={data.eps} onChange={(e) => set("eps", e.target.value)} placeholder="Ej. Sura, Nueva EPS…" />
      </label>

      <label className="form-field">
        <span>Médico tratante</span>
        <input value={data.medicoTratante} onChange={(e) => set("medicoTratante", e.target.value)} placeholder="Nombre del cirujano" />
      </label>

      <label className="form-field">
        <span>Tipo de anestesia</span>
        <input
          list="tipo-anestesia-list"
          value={data.tipoAnestesia}
          onChange={(e) => set("tipoAnestesia", e.target.value)}
          placeholder="Selecciona o escribe"
          autoComplete="off"
        />
        <datalist id="tipo-anestesia-list">
          {TIPOS_ANESTESIA.map((t) => <option key={t} value={t} />)}
        </datalist>
      </label>

      <label className="form-field">
        <span>Cirugía / Procedimiento *</span>
        <input
          list="cirugias-catalog"
          value={data.cirugia}
          onChange={(e) => set("cirugia", e.target.value)}
          className={invalid("cirugia")}
          placeholder="Escribe o elige del catálogo"
          autoComplete="off"
        />
        <datalist id="cirugias-catalog">
          {CIRUGIAS_COMUNES.map((c) => <option key={c} value={c} />)}
        </datalist>
      </label>

      <label className="form-field">
        <span>Fecha programada *</span>
        <input type="date" value={data.fecha} onChange={(e) => set("fecha", e.target.value)} className={invalid("fecha")} />
      </label>

      <label className="form-field">
        <span>Teléfono *</span>
        <input value={data.telefono} onChange={(e) => set("telefono", e.target.value)} className={invalid("telefono")} />
      </label>

      <label className="form-field">
        <span>Quien atiende *</span>
        <input value={data.programador} onChange={(e) => set("programador", e.target.value)} className={invalid("programador")} />
      </label>

      <label className="form-field form-field--full">
        <span>Diagnóstico CIE-10 principal *</span>
        <Cie10Search
          code={data.cie10}
          description={data.diagnostico}
          invalid={errors.has("diagnostico")}
          onSelect={(code, desc) => {
            set("cie10", code);
            set("diagnostico", desc);
          }}
        />
      </label>

      <div className="form-field form-field--full">
        <span className="form-field-label">Diagnósticos adicionales ({data.diagnosticosExtra.length})</span>
        {data.diagnosticosExtra.length > 0 && (
          <div className="dx-chips">
            {data.diagnosticosExtra.map((dx, i) => (
              <div key={`${dx.code}-${i}`} className="dx-chip">
                <span className="dx-chip-code">{dx.code}</span>
                <span className="dx-chip-desc">{dx.description}</span>
                <button
                  type="button"
                  className="dx-chip-remove"
                  onClick={() => removeExtra(i)}
                  aria-label={`Quitar diagnóstico ${dx.code}`}
                  title="Quitar"
                >×</button>
              </div>
            ))}
          </div>
        )}

        {showExtraForm ? (
          <div className="dx-add-inline">
            <Cie10Search
              code=""
              description=""
              onSelect={addDiagnostico}
            />
            <button type="button" className="btn btn-sm btn-secondary mt-8" onClick={() => setShowExtraForm(false)}>
              Cancelar
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-secondary mt-8"
            onClick={() => setShowExtraForm(true)}
            disabled={!data.cie10}
            title={!data.cie10 ? "Selecciona primero el diagnóstico principal" : "Agregar otro diagnóstico CIE-10"}
          >
            ➕ Agregar otro diagnóstico
          </button>
        )}
      </div>

      <label className="form-field form-field--full">
        <span>Observaciones</span>
        <textarea rows={3} value={data.observaciones} onChange={(e) => set("observaciones", e.target.value)} />
      </label>
    </div>
  );
}
